// src/pages/super-admin/GeneralAccountListPage.js - 일반 회원 계정 리스트뷰

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faUsers } from '@fortawesome/free-solid-svg-icons';
import { createAccountTokenTopup, getEnterpriseAccounts, updateEnterpriseAccount } from '../../services/api';
import './EnterpriseListPage.scss';

const AUTH_TYPE_LABELS = {
  google: 'Google SSO',
  individual: '개인',
};

const PLAN_LABELS = {
  free: 'Free',
  basic: 'Basic',
  premium: 'Premium',
};

const PLAN_OPTIONS = [
  { value: 'free', label: 'Free' },
  { value: 'basic', label: 'Basic' },
  { value: 'premium', label: 'Premium' },
];

const PLAN_BADGE_CLASS = {
  free: 'sa-badge-starter',
  basic: 'sa-badge-starter',
  premium: 'sa-badge-pro',
};

const formatTokenCount = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return num.toLocaleString('ko-KR');
};

function GeneralAccountListPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [authTypeFilter, setAuthTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [planDrafts, setPlanDrafts] = useState({});
  const [savingAccountId, setSavingAccountId] = useState(null);
  const [topupAccount, setTopupAccount] = useState(null);
  const [topupTokens, setTopupTokens] = useState('');
  const [topupNote, setTopupNote] = useState('');
  const [topupSaving, setTopupSaving] = useState(false);
  const [topupError, setTopupError] = useState(null);

  const perPage = 20;

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const baseParams = { per_page: 100 };
      if (search.trim()) baseParams.search = search.trim();
      if (authTypeFilter) baseParams.account_type = authTypeFilter;

      const firstPage = await getEnterpriseAccounts({ ...baseParams, page: 1 });
      const totalPages = firstPage.total_pages || 1;
      const restPages = await Promise.all(
        Array.from({ length: Math.max(totalPages - 1, 0) }, (_, index) =>
          getEnterpriseAccounts({ ...baseParams, page: index + 2 })
        )
      );
      const rawAccounts = [firstPage, ...restPages].flatMap((data) => (
        data.accounts || data.items || data.users || []
      ));
      const visibleAccounts = rawAccounts.filter((account) => {
        if (account.tier === 'super' || account.account_type === 'business') return false;
        if (authTypeFilter === 'google') return account.auth_type === 'google' || !!account.google_id;
        if (authTypeFilter === 'individual') return account.auth_type === 'individual' || account.account_type === 'individual';
        return true;
      });
      setAccounts(visibleAccounts);
      setTotalCount(visibleAccounts.length);
      setPlanDrafts(
        visibleAccounts.reduce((acc, account) => {
          acc[account.id] = account.plan_code || account.tier || 'free';
          return acc;
        }, {})
      );
    } catch (err) {
      console.error('일반 회원 목록 조회 실패:', err);
      setError('일반 회원 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [search, authTypeFilter]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const pagedAccounts = useMemo(() => {
    const start = (page - 1) * perPage;
    return accounts.slice(start, start + perPage);
  }, [accounts, page]);

  const totalPages = Math.max(Math.ceil(accounts.length / perPage), 1);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchAccounts();
  };

  const getAuthTypeLabel = (account) => {
    if (account.auth_type) return AUTH_TYPE_LABELS[account.auth_type] || account.auth_type;
    if (account.google_id || account.provider === 'google') return 'Google SSO';
    return '개인';
  };

  const handlePlanSave = async (account) => {
    const nextPlan = planDrafts[account.id] || 'free';
    if (nextPlan === (account.plan_code || account.tier || 'free')) return;
    try {
      setSavingAccountId(account.id);
      await updateEnterpriseAccount(account.id, { plan_code: nextPlan });
      await fetchAccounts();
    } catch (err) {
      console.error('플랜 변경 실패:', err);
      alert(err.message || '플랜 변경에 실패했습니다.');
    } finally {
      setSavingAccountId(null);
    }
  };

  const openTopupModal = (account) => {
    setTopupAccount(account);
    setTopupTokens('');
    setTopupNote('');
    setTopupError(null);
  };

  const closeTopupModal = () => {
    if (topupSaving) return;
    setTopupAccount(null);
    setTopupTokens('');
    setTopupNote('');
    setTopupError(null);
  };

  const handleTopupSubmit = async (event) => {
    event.preventDefault();
    if (!topupAccount) return;
    const weightedTokens = Number(topupTokens);
    if (!Number.isInteger(weightedTokens) || weightedTokens < 1) {
      setTopupError('충전 토큰은 1 이상의 정수로 입력해주세요.');
      return;
    }

    try {
      setTopupSaving(true);
      setTopupError(null);
      await createAccountTokenTopup(topupAccount.id, {
        weighted_tokens: weightedTokens,
        note: topupNote.trim() || undefined,
      });
      closeTopupModal();
      await fetchAccounts();
    } catch (err) {
      console.error('일반 회원 토큰 충전 실패:', err);
      setTopupError(err.message || '토큰 충전에 실패했습니다.');
    } finally {
      setTopupSaving(false);
    }
  };

  const getRemainingTokens = (account) => account.token_balance?.remaining_weighted_tokens;

  return (
    <div className="enterprise-list-page">
      <div className="sa-page-header">
        <div className="sa-page-header-left">
          <h1 className="sa-page-title">일반 계정 관리</h1>
          <p className="sa-page-subtitle">총 {totalCount}개의 일반 회원</p>
        </div>
      </div>

      <div className="sa-table-container">
        <div className="sa-table-toolbar">
          <div className="sa-table-toolbar-left">
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                className="sa-search-input"
                placeholder="이메일 또는 이름 검색..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button type="submit" className="sa-btn sa-btn-secondary sa-btn-sm">
                <FontAwesomeIcon icon={faSearch} />
              </button>
            </form>
            <select
              className="sa-filter-select"
              value={authTypeFilter}
              onChange={(e) => { setAuthTypeFilter(e.target.value); setPage(1); }}
            >
              <option value="">전체 가입형태</option>
              <option value="google">Google SSO</option>
              <option value="individual">개인</option>
            </select>
          </div>
        </div>

        {loading && <div className="sa-loading">불러오는 중...</div>}

        {error && (
          <div className="sa-empty">
            <div className="sa-empty-icon">⚠️</div>
            <div className="sa-empty-text">{error}</div>
            <button className="sa-btn sa-btn-secondary" onClick={fetchAccounts}>다시 시도</button>
          </div>
        )}

        {!loading && !error && (
          <>
            <table className="sa-table">
              <thead>
                <tr>
                  <th>이메일</th>
                  <th>이름</th>
                  <th>가입형태</th>
                  <th>체험 토큰</th>
                  <th>현재 플랜</th>
                  <th>가입일</th>
                  <th>플랜 변경</th>
                  <th>토큰</th>
                </tr>
              </thead>
              <tbody>
                {pagedAccounts.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="sa-empty">
                        <div className="sa-empty-icon"><FontAwesomeIcon icon={faUsers} style={{ fontSize: 36, color: '#d1d5db' }} /></div>
                        <div className="sa-empty-text">일반 회원 계정이 없습니다</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  pagedAccounts.map((account) => {
                    const currentPlan = account.plan_code || account.tier || 'free';
                    const draftPlan = planDrafts[account.id] || currentPlan;
                    const hasPlanChange = draftPlan !== currentPlan;
                    const remainingTokens = getRemainingTokens(account);

                    return (
                      <tr key={account.id}>
                        <td style={{ fontSize: 13, color: '#374151' }}>{account.email || '-'}</td>
                        <td style={{ fontWeight: 500, color: '#111827' }}>{account.name || '-'}</td>
                        <td>
                          <span className="sa-badge sa-badge-starter">
                            {getAuthTypeLabel(account)}
                          </span>
                        </td>
                        <td style={{ fontSize: 13, color: remainingTokens <= 0 ? '#dc2626' : '#374151', fontWeight: 500 }}>
                          {remainingTokens === undefined || remainingTokens === null
                            ? '-'
                            : formatTokenCount(remainingTokens)}
                        </td>
                        <td>
                          <span className={`sa-badge ${PLAN_BADGE_CLASS[currentPlan] || 'sa-badge-starter'}`}>
                            {PLAN_LABELS[currentPlan] || currentPlan || '-'}
                          </span>
                        </td>
                        <td style={{ fontSize: 13, color: '#6b7280' }}>
                          {account.created_at ? new Date(account.created_at).toLocaleDateString('ko-KR') : '-'}
                        </td>
                        <td>
                          <div className="enterprise-actions">
                            <select
                              className="sa-filter-select"
                              value={draftPlan}
                              onChange={(e) => setPlanDrafts((prev) => ({ ...prev, [account.id]: e.target.value }))}
                              disabled={savingAccountId === account.id}
                            >
                              {PLAN_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                            <button
                              className="sa-btn sa-btn-secondary sa-btn-sm enterprise-text-action"
                              onClick={() => handlePlanSave(account)}
                              disabled={!hasPlanChange || savingAccountId === account.id}
                            >
                              {savingAccountId === account.id ? '저장 중...' : '저장'}
                            </button>
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="sa-btn sa-btn-primary sa-btn-sm"
                            onClick={() => openTopupModal(account)}
                          >
                            <FontAwesomeIcon icon={faPlus} /> 충전
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="sa-pagination">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)}>이전</button>
                <span className="page-info">{page} / {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>다음</button>
              </div>
            )}
          </>
        )}
      </div>

      {topupAccount && (
        <div className="topup-modal-overlay" role="presentation" onClick={closeTopupModal}>
          <form
            className="topup-modal"
            onSubmit={handleTopupSubmit}
            onClick={(event) => event.stopPropagation()}
          >
            <h3>체험 토큰 충전</h3>
            <p style={{ margin: '0 0 12px', color: '#6b7280', fontSize: 13 }}>
              {topupAccount.email} · 잔액 {formatTokenCount(getRemainingTokens(topupAccount) ?? 0)}
            </p>
            <div className="topup-field">
              <label htmlFor="account-topup-tokens">Weighted tokens</label>
              <input
                id="account-topup-tokens"
                type="number"
                min="1"
                step="1"
                className="sa-search-input"
                value={topupTokens}
                onChange={(event) => setTopupTokens(event.target.value)}
                placeholder="200"
                disabled={topupSaving}
                autoFocus
              />
            </div>
            <div className="topup-field">
              <label htmlFor="account-topup-note">메모</label>
              <textarea
                id="account-topup-note"
                className="sa-search-input"
                value={topupNote}
                onChange={(event) => setTopupNote(event.target.value)}
                placeholder="CS 충전"
                disabled={topupSaving}
              />
            </div>
            {topupError && <div className="topup-error">{topupError}</div>}
            <div className="topup-actions">
              <button
                type="button"
                className="sa-btn sa-btn-secondary sa-btn-sm"
                onClick={closeTopupModal}
                disabled={topupSaving}
              >
                취소
              </button>
              <button type="submit" className="sa-btn sa-btn-primary sa-btn-sm" disabled={topupSaving}>
                {topupSaving ? '충전 중...' : '충전'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default GeneralAccountListPage;
